import { useState, useEffect, useCallback } from "react";
import { CalendarDays, Loader2, Plus, Trash2, Check, KeyRound, RotateCcw, Wrench, Users, CircleDot, ChevronLeft, ChevronRight, List, LayoutGrid } from "lucide-react";
import { apiFetch } from "@/portal/api";
import { toast } from "@/components/ui/sonner";
import { confirmDialog } from "@/portal/confirm";
import DateInput from "@/portal/components/DateInput";

const TYPE_META = {
  handover: { label: "Předání", icon: KeyRound, cls: "text-emerald-400 bg-emerald-500/15", dot: "bg-emerald-400" },
  return: { label: "Konec smlouvy", icon: RotateCcw, cls: "text-orange-400 bg-orange-500/15", dot: "bg-orange-400" },
  service: { label: "Servis", icon: Wrench, cls: "text-red-400 bg-red-500/15", dot: "bg-red-400" },
  meeting: { label: "Schůzka", icon: Users, cls: "text-sky-400 bg-sky-500/15", dot: "bg-sky-400" },
  other: { label: "Jiné", icon: CircleDot, cls: "text-zinc-400 bg-white/10", dot: "bg-zinc-400" },
};
const today = () => new Date().toISOString().slice(0, 10);
const fmt = (d) => new Date(d + "T00:00:00").toLocaleDateString("cs-CZ", { weekday: "short", day: "numeric", month: "long" });
const WEEKDAYS = ["Po", "Út", "St", "Čt", "Pá", "So", "Ne"];
const MONTHS = ["Leden", "Únor", "Březen", "Duben", "Květen", "Červen", "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"];
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const CalendarTab = ({ token }) => {
  const [data, setData] = useState({ events: [], derived: [] });
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", date: today(), time: "", type: "meeting", note: "" });
  const [saving, setSaving] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const [view, setView] = useState("agenda"); // agenda | month
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });

  const load = useCallback(async () => {
    try { setData(await apiFetch("/admin/events", { token })); } catch (e) { /* */ } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.title || !form.date) { toast.error("Vyplňte název a datum."); return; }
    setSaving(true);
    try { await apiFetch("/admin/events", { method: "POST", token, body: form }); toast.success("Událost přidána."); setForm({ title: "", date: today(), time: "", type: "meeting", note: "" }); load(); }
    catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };
  const toggleDone = async (ev) => { try { await apiFetch(`/admin/events/${ev.id}`, { method: "PATCH", token, body: { done: !ev.done } }); load(); } catch (e) { toast.error(e.message); } };
  const remove = async (ev) => { if (!(await confirmDialog({ title: "Smazat událost?", confirmText: "Smazat", danger: true }))) return; try { await apiFetch(`/admin/events/${ev.id}`, { method: "DELETE", token }); toast.success("Smazáno."); load(); } catch (e) { toast.error(e.message); } };

  const all = [...data.events.map((e) => ({ ...e, manual: true })), ...data.derived].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  const t = today();
  const upcoming = all.filter((e) => e.date >= t);
  const past = all.filter((e) => e.date < t);
  const list = showPast ? past.slice().reverse() : upcoming;
  const groups = list.reduce((acc, e) => { (acc[e.date] = acc[e.date] || []).push(e); return acc; }, {});

  // Month grid
  const byDay = all.reduce((acc, e) => { (acc[e.date] = acc[e.date] || []).push(e); return acc; }, {});
  const firstOfMonth = new Date(cursor.y, cursor.m, 1);
  const startWeekday = (firstOfMonth.getDay() + 6) % 7; // Monday=0
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(cursor.y, cursor.m, d));
  while (cells.length % 7 !== 0) cells.push(null);
  const prevMonth = () => setCursor((c) => { const m = c.m - 1; return m < 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m }; });
  const nextMonth = () => setCursor((c) => { const m = c.m + 1; return m > 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m }; });

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-orange-400 animate-spin" /></div>;

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="calendar-tab">
      {/* Add form */}
      <div className="lg:col-span-1">
        <div className="glass rounded-2xl p-6">
          <h3 className="font-['Cabinet_Grotesk'] text-lg font-bold mb-4 inline-flex items-center gap-2"><Plus className="w-5 h-5 text-orange-400" /> Nová událost</h3>
          <form onSubmit={add} className="space-y-3" data-testid="event-form">
            <input className="input-dark" placeholder="Název *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="event-title" />
            <div className="grid grid-cols-2 gap-2">
              <DateInput value={form.date} onChange={(v) => setForm({ ...form, date: v })} placeholder="Datum" testid="event-date" />
              <input type="time" className="input-dark" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="event-time" />
            </div>
            <select className="input-dark" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="event-type">
              {Object.entries(TYPE_META).map(([k, m]) => <option key={k} value={k} className="bg-[#0a0a0a]">{m.label}</option>)}
            </select>
            <textarea className="input-dark" rows={2} placeholder="Poznámka (volitelné)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="event-note" />
            <button type="submit" disabled={saving} className="btn-accent w-full inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="event-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Přidat do kalendáře
            </button>
          </form>
          <p className="text-[11px] text-zinc-600 mt-3">Předání a konce smluv se doplňují automaticky z pronájmů.</p>
        </div>
      </div>

      {/* Right side */}
      <div className="lg:col-span-2">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="inline-flex rounded-full border border-white/10 bg-white/5 p-1">
            <button onClick={() => setView("agenda")} className={`px-3 py-1.5 text-xs font-medium rounded-full inline-flex items-center gap-1.5 ${view === "agenda" ? "bg-orange-500 text-white" : "text-zinc-400 hover:text-white"}`} data-testid="calendar-view-agenda"><List className="w-3.5 h-3.5" /> Agenda</button>
            <button onClick={() => setView("month")} className={`px-3 py-1.5 text-xs font-medium rounded-full inline-flex items-center gap-1.5 ${view === "month" ? "bg-orange-500 text-white" : "text-zinc-400 hover:text-white"}`} data-testid="calendar-view-month"><LayoutGrid className="w-3.5 h-3.5" /> Měsíc</button>
          </div>
          {view === "agenda" && (
            <button onClick={() => setShowPast(!showPast)} className="text-xs text-zinc-400 hover:text-white border border-white/10 rounded-full px-3 py-1.5" data-testid="calendar-toggle-past">
              {showPast ? "Zobrazit nadcházející" : "Zobrazit proběhlé"}
            </button>
          )}
          {view === "month" && (
            <div className="inline-flex items-center gap-2">
              <button onClick={prevMonth} className="p-1.5 rounded-full border border-white/10 text-zinc-400 hover:text-white" data-testid="calendar-prev-month"><ChevronLeft className="w-4 h-4" /></button>
              <span className="text-sm font-medium w-36 text-center" data-testid="calendar-month-label">{MONTHS[cursor.m]} {cursor.y}</span>
              <button onClick={nextMonth} className="p-1.5 rounded-full border border-white/10 text-zinc-400 hover:text-white" data-testid="calendar-next-month"><ChevronRight className="w-4 h-4" /></button>
            </div>
          )}
        </div>

        {view === "agenda" ? (
          <>
            <h3 className="font-['Cabinet_Grotesk'] text-base font-bold inline-flex items-center gap-2 mb-3"><CalendarDays className="w-5 h-5 text-orange-400" /> {showPast ? "Proběhlé" : "Nadcházející"} ({list.length})</h3>
            {list.length === 0 ? (
              <div className="glass rounded-2xl p-8 text-center text-zinc-500" data-testid="calendar-empty">Žádné události.</div>
            ) : (
              <div className="space-y-5" data-testid="events-list">
                {Object.entries(groups).map(([date, evs]) => (
                  <div key={date}>
                    <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2">{fmt(date)}</p>
                    <div className="space-y-2">
                      {evs.map((ev) => {
                        const m = TYPE_META[ev.type] || TYPE_META.other;
                        const Icon = m.icon;
                        return (
                          <div key={ev.id} className={`glass rounded-xl p-3 flex items-center gap-3 ${ev.done ? "opacity-50" : ""}`} data-testid={`event-${ev.id}`}>
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${m.cls}`}><Icon className="w-4 h-4" /></div>
                            <div className="min-w-0 flex-1">
                              <p className={`text-sm font-medium truncate ${ev.done ? "line-through" : ""}`}>{ev.title}</p>
                              <p className="text-xs text-zinc-500 truncate">
                                {m.label}{ev.time ? ` · ${ev.time}` : ""}{ev.car ? ` · ${ev.car}` : ""}{ev.note ? ` · ${ev.note}` : ""}
                              </p>
                            </div>
                            {ev.manual ? (
                              <div className="flex items-center shrink-0">
                                <button onClick={() => toggleDone(ev)} className={`p-2 ${ev.done ? "text-emerald-400" : "text-zinc-500 hover:text-emerald-400"}`} title="Hotovo" data-testid={`event-done-${ev.id}`}><Check className="w-4 h-4" /></button>
                                <button onClick={() => remove(ev)} className="p-2 text-zinc-500 hover:text-red-400" title="Smazat" data-testid={`event-delete-${ev.id}`}><Trash2 className="w-4 h-4" /></button>
                              </div>
                            ) : (
                              <span className="text-[10px] text-zinc-600 shrink-0">auto</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="glass rounded-2xl p-3 sm:p-4" data-testid="calendar-month-grid">
            <div className="grid grid-cols-7 gap-1 mb-1">
              {WEEKDAYS.map((w) => <div key={w} className="text-center text-[11px] text-zinc-500 font-medium py-1">{w}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((d, i) => {
                if (!d) return <div key={`e-${i}`} className="min-h-[68px] rounded-lg bg-white/[0.02]" />;
                const key = ymd(d);
                const dayEvents = byDay[key] || [];
                const isToday = key === t;
                return (
                  <div key={key} className={`min-h-[68px] rounded-lg p-1.5 border ${isToday ? "border-orange-500/50 bg-orange-500/5" : "border-white/5 bg-white/[0.02]"}`} data-testid={`calendar-day-${key}`}>
                    <div className={`text-[11px] mb-1 ${isToday ? "text-orange-400 font-bold" : "text-zinc-500"}`}>{d.getDate()}</div>
                    <div className="space-y-0.5">
                      {dayEvents.slice(0, 3).map((ev) => {
                        const m = TYPE_META[ev.type] || TYPE_META.other;
                        return (
                          <div key={ev.id} className={`text-[10px] leading-tight truncate px-1 py-0.5 rounded ${m.cls} ${ev.done ? "opacity-50 line-through" : ""}`} title={`${ev.title}${ev.time ? ` · ${ev.time}` : ""}`} data-testid={`calendar-event-${ev.id}`}>
                            {ev.time ? `${ev.time} ` : ""}{ev.title}
                          </div>
                        );
                      })}
                      {dayEvents.length > 3 && <div className="text-[10px] text-zinc-500 px-1">+{dayEvents.length - 3} dalších</div>}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-white/5">
              {Object.entries(TYPE_META).map(([k, m]) => (
                <span key={k} className="inline-flex items-center gap-1.5 text-[11px] text-zinc-400"><span className={`w-2.5 h-2.5 rounded-sm ${m.dot}`} /> {m.label}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarTab;
